import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from './firebase';

/** Avoid repeated Cloud Function sync calls per browser session for the same uid. */
let crmStorageClaimSyncedForUid: string | null = null;

/** Roles that should receive crmStorageAccess — keep aligned with syncCrmStorageAccessClaim. */
export const CRM_STORAGE_ACCESS_ROLES = new Set([
  'designer',
  'architect',
  'engineer',
  'staff',
  'employee',
  'employee_shop',
  'employee_office',
  'admin',
  'cpa',
]);

/**
 * Sync `crmStorageAccess` JWT claim from Firestore role (Cloud Function), then refresh the token.
 * Storage rules read this claim first so architect/engineer uploads (project files, comment images)
 * are not blocked when rule-side Firestore reads lag or the token is stale.
 */
export async function ensureCrmStorageAccessBeforeUpload(force = false): Promise<void> {
  const u = auth.currentUser;
  if (!u || u.isAnonymous) return;
  try {
    if (force || crmStorageClaimSyncedForUid !== u.uid) {
      const functions = getFunctions(undefined, 'us-central1');
      const sync = httpsCallable<Record<string, never>, { crmStorageAccess?: boolean }>(
        functions,
        'syncCrmStorageAccessClaim',
      );
      await sync({});
      crmStorageClaimSyncedForUid = u.uid;
    }
  } catch {
    crmStorageClaimSyncedForUid = null;
  }
  await u.getIdToken(true);
}

/** Fire-and-forget after sign-in for design partners and internal CRM roles. */
export function prefetchCrmStorageAccessForRole(role: string | undefined): void {
  if (!role || !CRM_STORAGE_ACCESS_ROLES.has(String(role).trim().toLowerCase())) return;
  void ensureCrmStorageAccessBeforeUpload().catch(() => {});
}

/** Call after sign-out so the next user always re-syncs claims. */
export function resetCrmStorageAccessSyncCache(): void {
  crmStorageClaimSyncedForUid = null;
}
